import axios from 'axios';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { generateUrl } from '@shop/utils/Routing';

interface SearchResultProduct {
  uri: string;
  name: string;
}

interface AutocompleteResponse {
  terms: string[];
  products: SearchResultProduct[];
}

const useSearchStore = defineStore(
  'search',
  () => {
    const historyItems = ref<string[]>([]);
    const suggestions = ref<string[]>([]);
    const products = ref<SearchResultProduct[]>([]);
    const isLoading = ref(false);

    const MAX_HISTORY_ITEMS = 10;
    // Mirrors SearchService::AUTOCOMPLETE_QUERY_MIN_LENGTH (server rejects shorter terms).
    const SUGGEST_MIN_LENGTH = 3;

    const addToHistory = (searchTerm: string) => {
      if (!searchTerm.trim()) {
        return;
      }
      const searchTermTrimmed = searchTerm.trim();
      const index = historyItems.value.indexOf(searchTermTrimmed);
      if (index > -1) {
        historyItems.value.splice(index, 1);
      }
      historyItems.value.unshift(searchTermTrimmed);
      if (historyItems.value.length > MAX_HISTORY_ITEMS) {
        historyItems.value.pop();
      }
    };

    const clearHistory = () => {
      historyItems.value = [];
    };

    const search = async (searchTerm: string) => {
      if (searchTerm.trim().length < SUGGEST_MIN_LENGTH) {
        suggestions.value = [];
        products.value = [];
        return;
      }

      isLoading.value = true;

      const SUGGEST_ENDPOINT = generateUrl('get_suggests_autocomplete');

      try {
        const response = await axios.get<AutocompleteResponse>(SUGGEST_ENDPOINT, {
          params: {
            query: searchTerm.trim(),
          },
        });

        suggestions.value = response.data.terms || [];
        products.value = response.data.products || [];
      } catch {
        suggestions.value = [];
        products.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    const clearResults = () => {
      suggestions.value = [];
      products.value = [];
    };

    return {
      historyItems,
      suggestions,
      products,
      isLoading,
      addToHistory,
      clearHistory,
      search,
      clearResults,
    };
  },
  {
    persist: {
      storage: localStorage,
      key: 'search',
      pick: ['historyItems'],
    },
  }
);

export default useSearchStore;
